import type { Map as MapLibreMap, LngLatBoundsLike } from "maplibre-gl";
import { bbox, featureCollection } from "@wondermaps/maps";
import type { Layer } from "../MapCanvas";

// Constant for POI layer IDs used across setup and events
export const POI_LAYER_IDS = ["pois-tier1", "pois-tier2", "pois-tier3"] as const;

/**
 * Convert accuracy in meters to pixels at current zoom level
 * Used for user location accuracy circle rendering
 */
export function getMeterToPixels(map: MapLibreMap, lat: number, accuracyMeters: number): number {
  const zoom = map.getZoom();
  const metersPerPixel = (40075016.686 * Math.cos(lat * Math.PI / 180)) / (512 * Math.pow(2, zoom));
  return accuracyMeters / metersPerPixel;
}

/**
 * Fit map bounds to layer geometries (excluding vignette layers)
 */
export function fitMapBounds(map: MapLibreMap, layers: Layer[], fitBoundsMaxZoom = 17): void {
  const allGeometries = layers
    .filter((l) => l.geom && l.layerType !== "vignette")
    .map((l) => ({
      type: "Feature" as const,
      properties: {},
      geometry: l.geom!,
    }));

  if (allGeometries.length > 0) {
    const collection = featureCollection(allGeometries);
    const bounds = bbox(collection);
    map.fitBounds(
      [
        [bounds[0], bounds[1]],
        [bounds[2], bounds[3]],
      ],
      { padding: 50, maxZoom: fitBoundsMaxZoom }
    );
  }
}

/** ~400m buffer in degrees at mid-latitudes */
const BOUNDS_BUFFER_DEG = 0.004;

/**
 * Compute padded maxBounds from layer geometries (excluding vignette).
 * Returns null if no layers have geometry.
 */
export function computeMaxBounds(layers: Layer[]): LngLatBoundsLike | null {
  const allGeometries = layers
    .filter((l) => l.geom && l.layerType !== "vignette")
    .map((l) => ({
      type: "Feature" as const,
      properties: {},
      geometry: l.geom!,
    }));

  if (allGeometries.length === 0) return null;

  const bounds = bbox(featureCollection(allGeometries));
  return [
    [bounds[0] - BOUNDS_BUFFER_DEG, bounds[1] - BOUNDS_BUFFER_DEG],
    [bounds[2] + BOUNDS_BUFFER_DEG, bounds[3] + BOUNDS_BUFFER_DEG],
  ];
}
